import { useEffect, useState, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { FormLayout } from '../components/FormLayout';
import { MarkdownEditor } from '../components/MarkdownEditor';
import type { Activity, Car, Paginated, SingleResponse } from '../api/types';

const ACTIVITY_TYPE_KEYS = [
  'garage_sale',
  'trade_offer',
  'need_a_hand',
  'hosting_event',
  'car_show',
  'repair',
  'milestone',
];

export function NewActivity() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const preselectedCarId = searchParams.get('carId');
  const [type, setType] = useState('garage_sale');
  const [carId, setCarId] = useState<number | ''>(preselectedCarId ? Number(preselectedCarId) : '');
  const [summary, setSummary] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [locationName, setLocationName] = useState('');
  const [cars, setCars] = useState<Car[]>([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    apiFetch<Paginated<Car>>(`/api/v1/cars?filter[user_id]=${user.id}&per_page=50`).then((res) => {
      setCars(res.data);
      if (!preselectedCarId && res.data[0]) setCarId(res.data[0].id);
    });
  }, [user, preselectedCarId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await apiFetch<SingleResponse<Activity>>('/api/v1/activities', {
        method: 'POST',
        body: JSON.stringify({
          type,
          car_id: carId || null,
          summary,
          scheduled_at: scheduledAt || null,
          location_name: locationName || null,
          is_public: true,
        }),
      });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message ?? t('newActivity.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <FormLayout
        header={<PageHeader />}
        title={t('newActivity.title')}
        error={error || null}
        form={
          <>
            <FormControl fullWidth>
              <InputLabel id="type-label">{t('newActivity.activityType')}</InputLabel>
              <Select
                labelId="type-label"
                label={t('newActivity.activityType')}
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {ACTIVITY_TYPE_KEYS.map((key) => (
                  <MenuItem key={key} value={key}>
                    {t(`activityType.${key}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="car-label">{t('newActivity.selectSubject')}</InputLabel>
              <Select
                labelId="car-label"
                label={t('newActivity.selectSubject')}
                value={carId}
                onChange={(e) => setCarId(typeof e.target.value === 'number' ? e.target.value : '')}
              >
                <MenuItem value="">{t('newActivity.noCar')}</MenuItem>
                {cars.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.nickname ? `${c.nickname} (${c.year} ${c.make} ${c.model})` : `${c.year} ${c.make} ${c.model}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('newActivity.time')}
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label={t('newActivity.place')}
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              fullWidth
            />
          </>
        }
        aside={
          <MarkdownEditor
            label={t('newActivity.summary')}
            value={summary}
            onChange={setSummary}
            rows={10}
            required
          />
        }
        footer={
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained" size="large" disabled={submitting}>
              {submitting ? t('newActivity.submitting') : t('common.submit')}
            </Button>
          </Stack>
        }
      />
    </Box>
  );
}
